import { useState, useEffect, useCallback } from 'react'
import { unifiedSlotService } from '../services/volunteerSlotService'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { logger } from '@/utils/logger'
import { getSchoolYearDateBounds } from '@/utils/schoolYearHelpers'
import type { VolunteerSlot } from '../types/slot'

export function useVolunteerSlots(schoolYear: string = '2026-2027') {
  const { user, isAdmin } = useAuth()
  const [slots, setSlots] = useState<VolunteerSlot[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const loadSlots = useCallback(async () => {
    logger.info('Starting to load volunteer slots', { component: 'useVolunteerSlots', action: 'loadSlots', data: { schoolYear } })
    setIsLoading(true)
    
    try {
      const bounds = getSchoolYearDateBounds(schoolYear)
      const { data, error } = await supabase
        .from('volunteer_slots')
        .select('*')
        .gte('date', bounds.startDate)
        .lte('date', bounds.endDate)
        .order('date', { ascending: true })
        .order('class_name', { ascending: true })

      if (error) {
        throw error
      }

      const transformedSlots = (data || []).map(slot => ({
        ...slot,
        displayDate: slot.date.split('-').reverse().join('.'),
        originalDate: slot.date,
        availableClasses: [
          'כיתה א-1', 'כיתה א-2', 'כיתה א-3', 'כיתה א-4',
          'כיתה ב-1', 'כיתה ב-2', 'כיתה ב-3', 'כיתה ב-4', 
          'כיתה ג-1', 'כיתה ג-2', 'כיתה ג-3', 'כיתה ג-4',
          'כיתה ד-1', 'כיתה ד-2', 'כיתה ד-3', 'כיתה ד-4',
          'כיתה ה-1', 'כיתה ה-2', 'כיתה ה-3', 'כיתה ה-4'
        ]
      }))
      
      setSlots(transformedSlots)
    } catch (error) {
      console.error('❌ Failed to load slots:', error)
      setSlots([])
    } finally {
      setIsLoading(false)
    }
  }, [schoolYear])

  const lockSlot = useCallback(async (slotId: string) => {
    if (!user?.email) {
      return { success: false, error: 'Not authenticated' }
    }
    return await unifiedSlotService.lockSlot(slotId)
  }, [user?.email])

  const unlockSlot = useCallback(async (slotId: string) => {
    return await unifiedSlotService.unlockSlot(slotId)
  }, [])

  const registerForSlot = useCallback(async (
    slotId: string,
    userName: string,
    phoneNumber: string,
    email?: string,
    siblingClasses?: string[]
  ) => {
    return await unifiedSlotService.registerForSlot(slotId, userName, phoneNumber, email, siblingClasses)
  }, [])

  const cancelRegistration = useCallback(async (slotId: string) => {
    return await unifiedSlotService.cancelRegistration(slotId)
  }, [])

  const canDeleteSlot = useCallback((slot: VolunteerSlot) => {
    if (!user?.email) return false
    const isMySlot = slot.volunteer_email === user.email
    return isMySlot || isAdmin
  }, [user?.email, isAdmin])

  const isSlotLocked = useCallback((slot: VolunteerSlot) => {
    if (!slot.locked_by || !slot.lock_expires_at) return false
    return new Date(slot.lock_expires_at) > new Date()
  }, [])

  const isSlotLockedByMe = useCallback((slot: VolunteerSlot) => {
    return slot.locked_by === user?.email && isSlotLocked(slot)
  }, [user?.email, isSlotLocked])

  const getLockTimeRemaining = useCallback((slot: VolunteerSlot) => {
    if (!isSlotLocked(slot)) return 0
    const now = new Date()
    const expiresAt = new Date(slot.lock_expires_at!)
    return Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000))
  }, [isSlotLocked])

  useEffect(() => {
    loadSlots()
  }, [loadSlots])

  const slotIdsKey = slots.map(s => s.id).join(',')

  useEffect(() => {
    if (!slotIdsKey) return

    const unsubscribeFunctions: (() => void)[] = []
    slots.forEach(slot => {
      const unsubscribe = unifiedSlotService.subscribe(slot.id, (updatedSlot) => {
        setSlots(prevSlots => 
          prevSlots.map(s => 
            s.id === updatedSlot.id ? { 
              ...s,
              ...updatedSlot,
              displayDate: s.displayDate,
              availableClasses: s.availableClasses
            } : s
          )
        )
      })
      unsubscribeFunctions.push(unsubscribe)
    })

    return () => {
      unsubscribeFunctions.forEach(fn => fn())
    }
  }, [slotIdsKey])

  return {
    slots,
    isLoading,
    loadSlots,
    lockSlot,
    unlockSlot,
    registerForSlot,
    cancelRegistration,
    canDeleteSlot,
    isSlotLocked,
    isSlotLockedByMe,
    getLockTimeRemaining
  }
}
